/*
 * <coverage-verification> — "Start by verifying your coverage" lead form.
 *
 * Captures First / Last / Email / Phone and — once a routing worker is configured
 * — State + Insurance provider, then routes to one of THREE destinations. Same
 * decision the insurance-modal Hybrid block makes (assets/insurance-modal-hybrid.js),
 * on a page instead of in a modal:
 *
 *   1. Medicare / Medicaid / Medi-Cal  → Dreem worker → iSleep booking portal.
 *      Reached by the explicit "Continue With Medicare" button AND by picking a
 *      Medicare-ish payer under the private button. Canvas cannot serve Medicare,
 *      so these skip /route entirely (no Bridge call, no intake slot consumed),
 *      matching Insurance Modal v2.
 *   2. In-house capable + capacity     → Canvas Zoho intake (canvas_form_url,
 *      ?PrimaryPayor=&PayorID=), route "canvas".
 *   3. Everything else                 → the STANDARD new-patient questionnaire
 *      (new_patient_form_url) — i.e. exactly where the private button already
 *      went before routing existed.
 *
 * (3) is the one that's easy to get wrong. A commercial-payer shopper we can't
 * serve in house — no Bridge coverage, state not opened, no NP capacity, stale
 * feed — belongs in the usual insurance intake, NOT the iSleep Medicare flow.
 * Because (1) never reaches /route, every /route reply is a non-Medicare shopper,
 * which is what makes the worker's "isleep" route safe to render as the standard
 * questionnaire. Read that route value as "not in-house"; `intake_destination` on
 * the analytics event carries the true destination.
 *
 * The routing decision is deliberately NOT made here. Whether we can see a
 * patient in house depends on Bridge coverage for BOTH provider types AND a real
 * bookable Nurse Practitioner slot in their state — shared, cached signals no
 * browser can hold. The worker owns it; this file renders the answer.
 * See bridge-proxy/README.md.
 *
 * STATE HAND-OFF. The shopper picked a state on the previous page
 * (insurance-coverage), which appends the 2-letter code as `data-state-param`
 * (default `state`). We prefill the dropdown from it and leave it editable, so
 * the normal journey never asks twice — while direct arrivals (ads, bookmarks,
 * a shared link) still get a working state picker instead of a dead end.
 *
 * PROGRESSIVE ENABLEMENT. With no `data-routing-worker-url` the section renders
 * no state/provider fields and no panels, and the private button behaves exactly
 * as it did before routing existed. Every routing branch below is therefore
 * guarded on `routingEnabled`, not just on the worker URL being truthy.
 *
 * ANALYTICS. Two event names on purpose:
 *   `coverage_verification`   the section's own funnel (private_insurance_click,
 *                             medicare_click) — pre-existing, unchanged.
 *   `insurance_modal_hybrid`  every routing event, with the SAME event_action and
 *                             parameter names the modal uses, so the existing GTM
 *                             triggers, GA4 tags and the Sheets logger pick this
 *                             surface up with no new configuration. A `surface`
 *                             param separates the two (absent = the modal).
 *                             See .claude/docs/insurance-modal-gtm-spec.md §8.
 *
 * Worker POSTs to the LEAD worker omit Content-Type so they stay CORS-"simple"
 * (no preflight); that worker parses the JSON body regardless (matches the
 * estimate worker). The routing worker does send JSON — it is CORS-configured.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 749;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* Payers that always route to iSleep (Canvas cannot serve Medicare), checked before the worker call. */
  var ISLEEP_NAME_RE = /medicare|medicaid|medi-?cal/i;

  var SEARCH_DEBOUNCE_MS = 250;
  var SEARCH_MIN_CHARS = 2;

  /* Intake split (Theme settings > Insurance intake split). Same sticky 0-99 bucket
     and storage key as Insurance Modal v2, so a visitor gets the same intake form on
     this page and in the modal. Falls back to a per-page-load bucket when storage
     is blocked. */
  var INTAKE_BUCKET_KEY = 'sd_intake_split_bucket';
  var memoryBucket = null;
  function intakeSplitBucket() {
    try {
      var stored = parseInt(window.localStorage.getItem(INTAKE_BUCKET_KEY), 10);
      if (stored >= 0 && stored < 100) return stored;
      var fresh = Math.floor(Math.random() * 100);
      window.localStorage.setItem(INTAKE_BUCKET_KEY, String(fresh));
      return fresh;
    } catch {
      if (memoryBucket === null) memoryBucket = Math.floor(Math.random() * 100);
      return memoryBucket;
    }
  }

  function appendParam(url, key, value) {
    if (!url || !value) return url;
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    return url + sep + encodeURIComponent(key) + '=' + encodeURIComponent(value);
  }

  class CoverageVerification extends HTMLElement {
    constructor() {
      super();
      this.selectedState = ''; /* 2-letter code — what Bridge expects */
      this.selectedPayerId = '';
      this.selectedPayerName = '';
      this.searchSeq = 0;
      this.searchTimer = null;
    }

    connectedCallback() {
      this.newPatientUrl = this.getAttribute('data-new-patient-url') || '';
      this.intakeSplitUrl = this.getAttribute('data-intake-split-url') || '';
      this.intakeSplitPercent = parseInt(this.getAttribute('data-intake-split-percent'), 10) || 0;
      this.intakeSplitStates = (this.getAttribute('data-intake-split-states') || '')
        .split(',')
        .map(function (code) { return code.trim().toUpperCase(); })
        .filter(Boolean);
      this.leadWorkerUrl = this.getAttribute('data-lead-worker-url') || '';
      this.dreemRedirectUrl = this.getAttribute('data-dreem-redirect-url') || '';

      this.routingWorkerUrl = (this.getAttribute('data-routing-worker-url') || '').replace(/\/$/, '');
      this.canvasFormUrl = this.getAttribute('data-canvas-form-url') || '';
      this.primaryPayorParam = this.getAttribute('data-primary-payor-param') || 'PrimaryPayor';
      this.payerParam = this.getAttribute('data-payer-param') || 'PayorID';
      this.stateParam = this.getAttribute('data-state-param') || 'state';
      this.checkingText = this.getAttribute('data-checking-text') || 'Checking coverage…';
      this.form = this.querySelector('.coverage-verification__form');
      this.firstNameInput = this.querySelector('input[name="first_name"]');
      this.lastNameInput = this.querySelector('input[name="last_name"]');
      this.emailInput = this.querySelector('input[name="email"]');
      this.phoneInput = this.querySelector('input[name="phone"]');
      this.inputs = [this.firstNameInput, this.lastNameInput, this.emailInput, this.phoneInput];
      this.privateBtn = this.querySelector('[data-private]');
      this.medicareBtn = this.querySelector('[data-medicare]');

      this.stateField = this.querySelector('[data-state-field]');
      this.stateDropdown = this.querySelector('[data-state-dropdown]');
      this.providerField = this.querySelector('[data-provider-field]');
      this.providerInput = this.querySelector('[data-provider-input]');
      this.providerList = this.querySelector('[data-autocomplete-list]');
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-panel]'));

      /* Both halves must be present, not just the URL — the fields are only
         rendered when the section has a worker configured, and a half-rendered
         DOM must not put the private button into a branch that needs a payer. */
      this.routingEnabled = !!(this.routingWorkerUrl && this.stateDropdown && this.providerInput);

      this.inputs.forEach(function (input) {
        if (!input) return;
        input.addEventListener('input', function () {
          var field = input.closest('.coverage-verification__field');
          if (field) field.classList.remove('has-error');
        });
      });

      if (this.routingEnabled) {
        this.initDropdown();
        this.initAutocomplete();
        this.prefillStateFromQuery();

        this.handleOutsideClick = function (e) {
          if (!e.target.closest('.sd-insurance-dropdown')) {
            this.closeDropdown();
            this.closeProviderList();
          }
        }.bind(this);
        document.addEventListener('click', this.handleOutsideClick);

        this.querySelectorAll('[data-panel-back]').forEach(
          function (btn) {
            btn.addEventListener('click', this.showForm.bind(this));
          }.bind(this)
        );
      }

      if (this.privateBtn) this.privateBtn.addEventListener('click', this.onPrivate.bind(this));
      if (this.medicareBtn) this.medicareBtn.addEventListener('click', this.onMedicare.bind(this));
    }

    disconnectedCallback() {
      if (this.handleOutsideClick) document.removeEventListener('click', this.handleOutsideClick);
    }

    isMobile() {
      return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    val(input) {
      return input ? input.value.trim() : '';
    }

    /* ---- State: inbound query param, then the dropdown ---- */

    /* The previous page already asked. Prefill rather than ask again — but leave
       the field editable, because the state that matters here is where the
       patient is treated and they may want to correct it. An unknown or
       malformed code is ignored, not surfaced as an error: the shopper simply
       sees an empty picker, which is also what direct traffic gets. */
    prefillStateFromQuery() {
      var code;
      try {
        code = (new URLSearchParams(window.location.search).get(this.stateParam) || '')
          .trim()
          .toUpperCase();
      } catch (err) {
        void err; /* URL parsing is best-effort; never block the form */
        return;
      }
      if (!code) return;
      /* Matched by iteration rather than an interpolated selector — the value is
         straight off the query string. */
      var option = Array.prototype.slice
        .call(this.stateDropdown.querySelectorAll('.sd-insurance-dropdown-option'))
        .filter(function (opt) {
          return opt.getAttribute('data-value') === code;
        })[0];
      if (!option) return;
      this.applyStateOption(option);
      this.pushHybrid({
        event_action: 'state_prefilled',
        modal_step: 'step_1_selection',
        shipping_state: code,
      });
    }

    initDropdown() {
      var root = this.stateDropdown;
      var trigger = root.querySelector('[data-dropdown-trigger]');
      var self = this;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (root.classList.contains('is-open')) {
          self.closeDropdown();
        } else {
          self.closeProviderList();
          self.openDropdown();
        }
      });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeDropdown();
      });

      root.querySelectorAll('.sd-insurance-dropdown-option').forEach(function (opt) {
        opt.addEventListener('click', function () {
          self.selectStateOption(opt);
        });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.selectStateOption(opt);
          }
        });
      });
    }

    openDropdown() {
      var root = this.stateDropdown;
      root.classList.add('is-open');
      root.querySelector('[data-dropdown-trigger]').setAttribute('aria-expanded', 'true');
      root.querySelector('[data-dropdown-list]').hidden = false;
    }

    closeDropdown() {
      var root = this.stateDropdown;
      if (!root) return;
      root.classList.remove('is-open');
      var trigger = root.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      var list = root.querySelector('[data-dropdown-list]');
      if (list) list.hidden = true;
    }

    /* Shared by the prefill and a real click, so both leave identical state. */
    applyStateOption(opt) {
      var root = this.stateDropdown;
      var valueEl = root.querySelector('[data-dropdown-value]');
      valueEl.textContent = opt.querySelector('.sd-insurance-dropdown-option-title').textContent;
      valueEl.classList.remove('is-placeholder');
      root.querySelectorAll('.sd-insurance-dropdown-option').forEach(function (o) {
        o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
      });
      this.selectedState = opt.getAttribute('data-value') || '';
      /* Provider search is state-scoped, so it stays disabled until now. */
      this.providerInput.disabled = false;
      if (this.stateField) this.stateField.classList.remove('has-error');
    }

    selectStateOption(opt) {
      var previous = this.selectedState;
      this.applyStateOption(opt);
      this.closeDropdown();
      /* A different state can mean a different payer entity, and the id we hold
         was resolved against the old one. Drop it rather than send a stale pair. */
      if (previous && previous !== this.selectedState) this.clearPayer();
      this.pushHybrid({
        event_action: 'state_selected',
        modal_step: 'step_1_selection',
        shipping_state: this.selectedState,
      });
    }

    /* ---- Provider autocomplete (Bridge payer search via the worker) ---- */

    clearPayer() {
      this.selectedPayerId = '';
      this.selectedPayerName = '';
      this.providerInput.value = '';
      this.providerList.innerHTML = '';
      this.closeProviderList();
      this.searchSeq++;
    }

    initAutocomplete() {
      var self = this;

      this.providerInput.addEventListener('input', function () {
        /* Any edit invalidates the prior pick — a free-text payer name is not a
           payer id, and the worker needs the id. */
        self.selectedPayerId = '';
        self.selectedPayerName = '';
        if (self.providerField) self.providerField.classList.remove('has-error');
        self.scheduleSearch(self.providerInput.value.trim());
      });

      this.providerInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.closeProviderList();
        } else if (e.key === 'ArrowDown') {
          var first = self.providerList.querySelector(
            '.sd-insurance-dropdown-option:not(.is-message)'
          );
          if (first) {
            e.preventDefault();
            first.focus();
          }
        }
      });
      /* The list opens only in response to typing, never on focus — otherwise
         refocusing right after a selection would immediately reopen the results. */
    }

    scheduleSearch(query) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (query.length < SEARCH_MIN_CHARS) {
        this.closeProviderList();
        return;
      }
      var self = this;
      this.searchTimer = setTimeout(function () {
        self.searchPayers(query);
      }, SEARCH_DEBOUNCE_MS);
    }

    async searchPayers(query) {
      var seq = ++this.searchSeq;
      this.renderProviderMessage('Searching…');
      try {
        var res = await fetch(this.routingWorkerUrl + '/payers/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: query, limit: 10 }),
        });
        /* Guard every await: a slower earlier query must not overwrite a newer
           one's results. */
        if (seq !== this.searchSeq) return;
        if (!res.ok) throw new Error('Payer search failed: ' + res.status);
        var data = await res.json();
        if (seq !== this.searchSeq) return;
        this.renderProviderResults((data && data.items) || []);
      } catch (err) {
        if (seq !== this.searchSeq) return;
        console.error('Bridge payer search failed:', err);
        this.renderProviderMessage('We couldn’t load providers. Try again.');
      }
    }

    renderProviderResults(items) {
      if (!items.length) {
        this.renderProviderMessage('No matching providers found.');
        return;
      }
      var self = this;
      this.providerList.innerHTML = '';
      items.forEach(function (payer) {
        var li = document.createElement('li');
        li.className = 'sd-insurance-dropdown-option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.setAttribute('aria-selected', 'false');
        li.dataset.payerId = payer.id || '';
        li.dataset.payerName = payer.name || '';
        var title = document.createElement('span');
        title.className = 'sd-insurance-dropdown-option-title';
        title.textContent = payer.name || '';
        li.appendChild(title);
        if (payer.hint) {
          var desc = document.createElement('span');
          desc.className = 'sd-insurance-dropdown-option-desc';
          desc.textContent = payer.hint;
          li.appendChild(desc);
        }
        li.addEventListener('click', function () {
          self.selectPayer(li);
        });
        li.addEventListener('keydown', function (e) {
          self.onOptionKeydown(e, li);
        });
        self.providerList.appendChild(li);
      });
      this.openProviderList();
    }

    renderProviderMessage(message) {
      this.providerList.innerHTML = '';
      var li = document.createElement('li');
      li.className = 'sd-insurance-dropdown-option is-message';
      li.setAttribute('aria-disabled', 'true');
      var title = document.createElement('span');
      title.className = 'sd-insurance-dropdown-option-title';
      title.textContent = message;
      li.appendChild(title);
      this.providerList.appendChild(li);
      this.openProviderList();
    }

    onOptionKeydown(e, li) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.selectPayer(li);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        var next = li.nextElementSibling;
        if (next && !next.classList.contains('is-message')) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = li.previousElementSibling;
        if (prev && !prev.classList.contains('is-message')) prev.focus();
        else this.providerInput.focus();
      } else if (e.key === 'Escape') {
        this.closeProviderList();
        this.providerInput.focus();
      }
    }

    selectPayer(li) {
      this.selectedPayerId = li.dataset.payerId || '';
      this.selectedPayerName = li.dataset.payerName || '';
      this.providerInput.value = this.selectedPayerName;
      this.closeProviderList();
      /* Drop the rendered results so nothing can re-surface them after select. */
      this.providerList.innerHTML = '';
      if (this.providerField) this.providerField.classList.remove('has-error');
      this.pushHybrid({
        event_action: 'insurance_selected',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
      });
      this.providerInput.focus();
    }

    openProviderList() {
      this.providerList.hidden = false;
      this.providerInput.setAttribute('aria-expanded', 'true');
    }

    closeProviderList() {
      if (!this.providerList) return;
      this.providerList.hidden = true;
      this.providerInput.setAttribute('aria-expanded', 'false');
    }

    /* ---- Validation ---- */

    markField(field, ok, valid) {
      if (!field) return valid;
      if (ok) {
        field.classList.remove('has-error');
        return valid;
      }
      field.classList.add('has-error');
      return false;
    }

    /* `requireCoverage` is what separates the two buttons: the private path can't
       ask the worker anything without a state and a resolved payer id, while the
       Medicare path never calls the worker and must not gain new friction. */
    validate(requireCoverage) {
      var valid = true;
      var self = this;
      [
        { input: this.firstNameInput, ok: this.val(this.firstNameInput) !== '' },
        { input: this.lastNameInput, ok: this.val(this.lastNameInput) !== '' },
        { input: this.emailInput, ok: EMAIL_RE.test(this.val(this.emailInput)) },
        { input: this.phoneInput, ok: this.val(this.phoneInput) !== '' },
      ].forEach(function (check) {
        var field = check.input ? check.input.closest('.coverage-verification__field') : null;
        valid = self.markField(field, check.ok, valid);
      });

      if (requireCoverage) {
        valid = this.markField(this.stateField, !!this.selectedState, valid);
        /* The id, not the typed text: a free-text payer name is not a payer id,
           and /route needs the id. */
        valid = this.markField(this.providerField, !!this.selectedPayerId, valid);
      }

      if (!valid) {
        var firstError = this.querySelector('.coverage-verification__field.has-error');
        if (firstError) {
          var focusable = firstError.querySelector('input, [data-dropdown-trigger]');
          if (focusable) focusable.focus();
        }
      }
      return valid;
    }

    /* ---- Lead capture ---- */

    /* Best-effort — never blocks the redirect. */
    async postLead(url, extra) {
      if (!url) return;
      var first = this.val(this.firstNameInput);
      var last = this.val(this.lastNameInput);
      var payload = {
        name: (first + ' ' + last).trim(),
        first_name: first,
        last_name: last,
        email: this.val(this.emailInput),
        phone: this.val(this.phoneInput),
        source: 'sleepdoctor_embedded_insurance_redirect',
      };
      if (this.selectedState) payload.state = this.selectedState;
      if (this.selectedPayerName) payload.insurance_carrier = this.selectedPayerName;
      if (this.selectedPayerId) payload.payer_id = this.selectedPayerId;
      if (extra) {
        Object.keys(extra).forEach(function (key) {
          payload[key] = extra[key];
        });
      }
      try {
        await fetch(url, { method: 'POST', body: JSON.stringify(payload) });
      } catch (err) {
        /* swallow — reaching the destination form matters more than the ping */
        void err;
      }
    }

    /* ---- Redirect helpers ---- */

    /* The whole reason this page exists rather than sending shoppers straight to
       a form: the destination arrives pre-filled. */
    withPrefill(url) {
      url = appendParam(url, 'SSFirstName', this.val(this.firstNameInput));
      url = appendParam(url, 'SSLastName', this.val(this.lastNameInput));
      url = appendParam(url, 'SSEmail', this.val(this.emailInput));
      url = appendParam(url, 'SDphone', this.val(this.phoneInput));
      return url;
    }

    /* The standard questionnaire, or the Zoho Creator intake for the share of
       visitors the intake split selects. The Creator URL gets the standard URL's own
       query params (utm_*, SDtreatment, ...) unless it sets them itself; prefill and
       tracking ids are added afterwards exactly as for the standard form. */
    resolveStandardIntake() {
      var standard = { url: this.newPatientUrl, destination: 'standard_intake' };
      if (!this.intakeSplitUrl || this.intakeSplitPercent <= 0) return standard;
      if (this.intakeSplitStates.length && this.intakeSplitStates.indexOf(this.selectedState) === -1) {
        return standard;
      }
      if (intakeSplitBucket() >= this.intakeSplitPercent) return standard;
      var url = this.intakeSplitUrl;
      try {
        var target = new URL(this.intakeSplitUrl, window.location.origin);
        var source = new URL(this.newPatientUrl, window.location.origin);
        source.searchParams.forEach(function (value, key) {
          if (!target.searchParams.has(key)) target.searchParams.set(key, value);
        });
        url = target.toString();
      } catch {
        /* Unparseable URL: use the split URL as configured. */
      }
      return { url: url, destination: 'zoho_creator' };
    }

    redirect(url) {
      if (window.SDFormTracking && window.SDFormTracking.appendIds) {
        window.SDFormTracking.appendIds(url, function (finalUrl) {
          window.location.href = finalUrl;
        });
      } else {
        window.location.href = url;
      }
    }

    /* ---- "Continue With Employer or Private Insurance" ---- */

    async onPrivate(e) {
      e.preventDefault();
      if (!this.validate(this.routingEnabled)) return;

      this.pushDataLayer({
        event_action: 'private_insurance_click',
        shipping_state: this.selectedState,
        insurance_carrier: this.selectedPayerName,
      });

      /* A Medicare-ish payer picked under the private button is still a Medicare
         patient, and Canvas cannot serve Medicare: hand them to the Medicare path
         without asking the worker (no Bridge call, no intake slot consumed). */
      if (this.routingEnabled && ISLEEP_NAME_RE.test(this.selectedPayerName)) {
        await this.runMedicare(this.privateBtn, 'medicare_payer_selected');
        return;
      }

      this.setBusy(this.privateBtn, true);
      /* Deliberately not awaited yet: the lead POST rides alongside the /route
         call instead of in front of it, then is awaited below so it can't be
         cancelled by the navigation. */
      var leadPromise = this.postLead(this.leadWorkerUrl, { target: 'insurance_leads' });

      if (!this.routingEnabled) {
        await leadPromise;
        var plain = this.withPrefill(this.resolveStandardIntake().url);
        if (!plain) {
          this.setBusy(this.privateBtn, false);
          return;
        }
        this.redirect(plain);
        return;
      }

      this.setChecking(this.privateBtn, true);
      var data;
      try {
        data = await this.decideRoute();
      } catch (err) {
        console.error('Route decision failed:', err);
        this.pushHybrid({
          event_action: 'worker_error',
          modal_step: 'step_1_selection',
          error_phase: 'route',
        });
        this.setChecking(this.privateBtn, false);
        this.setBusy(this.privateBtn, false);
        this.showPanel('error');
        return;
      }

      if (data.route === 'ineligible') {
        this.setChecking(this.privateBtn, false);
        this.setBusy(this.privateBtn, false);
        this.showPanel('ineligible');
        return;
      }

      /* Anything that isn't in-house goes to the STANDARD questionnaire — not
         iSleep. Medicare short-circuited before the request was made, so every
         reply here is a non-Medicare shopper, and an unrecognized route lands
         here too: still a real destination, and the
         correct one for a commercial payer. */
      var isCanvas = data.route === 'canvas' && !!this.canvasFormUrl;
      /* The worker said in-house but no Canvas URL is configured. Still send them
         somewhere real — but say so, on the console AND on the event. Reporting
         this as a plain standard_intake would make a config error look like a
         legitimate routing decision, and nobody would ever find it. */
      var misconfigured = data.route === 'canvas' && !this.canvasFormUrl;
      if (misconfigured) {
        console.warn(
          'coverage-verification: route "canvas" but canvas_form_url is blank; ' +
            'falling back to the standard questionnaire.'
        );
      }
      var intake = isCanvas ? null : this.resolveStandardIntake();
      var target = isCanvas ? this.canvasFormUrl : intake.url;
      if (!target) {
        /* Nothing configured to redirect to — surface it rather than silently
           leaving the shopper on a spinning button. */
        console.warn('coverage-verification: no redirect URL for route "' + data.route + '".');
        this.setChecking(this.privateBtn, false);
        this.setBusy(this.privateBtn, false);
        this.showPanel('error');
        return;
      }
      if (isCanvas) {
        target = appendParam(target, this.primaryPayorParam, this.selectedPayerName);
        target = appendParam(target, this.payerParam, this.selectedPayerId);
      }
      target = this.withPrefill(target);

      this.pushHybrid({
        event_action: 'continue_with_insurance_click',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
        shipping_state: this.selectedState,
        intake_destination: isCanvas ? 'canvas' : intake.destination,
        intake_split_percent: this.intakeSplitPercent,
        route: data.route || '',
        fallback_reason: misconfigured ? 'canvas_url_missing' : '',
        redirect_url: target,
      });

      await leadPromise;
      /* Navigating away — leave the button in its loading state. */
      this.redirect(target);
    }

    async decideRoute() {
      this.pushHybrid({
        event_action: 'route_decision_started',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        shipping_state: this.selectedState,
      });
      var res = await fetch(this.routingWorkerUrl + '/route', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payerId: this.selectedPayerId,
          state: this.selectedState,
          payerName: this.selectedPayerName,
          /* No enabledStates: the worker's CANVAS_ENABLED_STATES is the only rollout list. */
        }),
      });
      if (!res.ok) throw new Error('Route decision failed: ' + res.status);
      var data = await res.json();
      this.pushHybrid({
        event_action: 'route_decided',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
        shipping_state: this.selectedState,
        route: data.route || '',
        reason: data.reason || '',
        in_house_capable: !!data.inHouseCapable,
        priority_state: !!data.priorityState,
      });
      return data;
    }

    /* ---- "Continue With Medicare" ---- */

    async onMedicare(e) {
      e.preventDefault();
      /* No coverage requirement here: this path never calls the worker, so a
         state and payer would be friction with nothing behind it. */
      if (!this.validate(false)) return;
      this.pushDataLayer({ event_action: 'medicare_click' });
      await this.runMedicare(this.medicareBtn, 'medicare_button');
    }

    async runMedicare(btn, reason) {
      this.setBusy(btn, true);

      await this.postLead(this.leadWorkerUrl, {
        target: 'referrals',
        medicare_type: this.selectedPayerName || 'medicare',
        referral_reason: reason,
      });

      /* After the POST, matching the modal's order — the referral is what the
         event claims happened. */
      this.pushHybrid({
        event_action: 'isleep_form_submitted',
        modal_step: 'step_2_isleep_form',
        coverage_type_selected: this.selectedPayerName || 'medicare',
        shipping_state: this.selectedState,
        intake_destination: 'isleep_medicare',
        isleep_reason: reason,
      });

      if (!this.dreemRedirectUrl) {
        this.setBusy(btn, false);
        return;
      }
      this.pushHybrid({
        event_action: 'isleep_redirect_initiated',
        modal_step: 'step_2_isleep_form',
        coverage_type_selected: this.selectedPayerName || 'medicare',
        redirect_url: this.dreemRedirectUrl,
      });
      window.location.href = this.dreemRedirectUrl;
    }

    /* ---- Panels ---- */

    /* One state at a time: the form and the outcome panels are mutually
       exclusive, so a shopper never sees an error above a live form. */
    showPanel(name) {
      if (this.form) this.form.hidden = true;
      this.panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-panel') !== name;
      });
      var shown = this.panels.filter(function (panel) {
        return !panel.hidden;
      })[0];
      if (shown) {
        var back = shown.querySelector('[data-panel-back]');
        if (back) back.focus();
      }
      if (name === 'ineligible') {
        this.pushHybrid({
          event_action: 'coverage_unavailable',
          modal_step: 'step_ineligible',
          insurance_carrier: this.selectedPayerName,
          shipping_state: this.selectedState,
        });
      }
    }

    /* Back from a panel: the selections stay intact so a retry is one edit away,
       but the payer is dropped — it's the thing that just failed, and keeping it
       would let the shopper resubmit the identical pair. */
    showForm() {
      this.panels.forEach(function (panel) {
        panel.hidden = true;
      });
      if (this.form) this.form.hidden = false;
      this.clearPayer();
      if (this.providerInput) this.providerInput.focus();
    }

    /* ---- Button state ---- */

    setBusy(btn, busy) {
      if (!btn) return;
      btn.disabled = busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    setChecking(btn, checking) {
      if (!btn) return;
      if (checking) {
        if (this.btnLabelBefore == null) this.btnLabelBefore = btn.textContent;
        btn.textContent = this.checkingText;
      } else if (this.btnLabelBefore != null) {
        btn.textContent = this.btnLabelBefore;
        this.btnLabelBefore = null;
      }
    }

    /* ---- Analytics ---- */

    pushDataLayer(params) {
      this.push('coverage_verification', params);
    }

    /* Routing events go out under the modal's event name with the modal's
       parameter names, so every existing GTM trigger, GA4 tag and the Sheets
       logger pick this surface up unchanged. `surface` is what tells the two
       apart downstream — the modal doesn't set it, so absent means modal. */
    pushHybrid(params) {
      this.push(
        'insurance_modal_hybrid',
        Object.assign({ surface: 'coverage_verification_page' }, params)
      );
    }

    push(eventName, params) {
      window.dataLayer = window.dataLayer || [];
      var payload = {
        event: eventName,
        device_type: this.isMobile() ? 'mobile' : 'desktop',
      };
      Object.keys(params).forEach(function (key) {
        payload[key] = params[key];
      });
      window.dataLayer.push(payload);
    }
  }

  if (!customElements.get('coverage-verification')) {
    customElements.define('coverage-verification', CoverageVerification);
  }
})();
